// src/features/bom/services/productionFloorApi.ts

import apiClient from '@/lib/api-client';

// ============================================================
// TYPES
// ============================================================

export interface ProductionFloor {
    id: string;
    tenantId: string;
    branchId: string;
    branchName?: string;
    name: string;
    floorType: 'kitchen' | 'bakery' | 'butchery' | 'bar' | 'general';
    manager: string | null;
    status: 'active' | 'inactive' | 'maintenance';
    description: string | null;
    contactPhone: string | null;
    contactEmail: string | null;
    isActive: boolean;
    recipeCount?: number;
    inventoryCount?: number;
    activeBatchCount?: number;
    createdAt: string;
    updatedAt: string;
}

export interface FloorInventoryItem {
    id: string;
    floorId: string;
    productId: string;
    productName?: string;
    productSku?: string;
    variantId: string | null;
    variantName?: string | null;
    quantityOnHand: string;
    uomId: string;
    uomName?: string;
    uomAbbreviation?: string;
    lastUpdated: string;
}

export interface FloorDashboard {
    floor: ProductionFloor;
    inventory: { totalItems: number; totalValue: number; lowStockItems: number };
    production: { todayBatches: number; activeBatches: number; completedBatches: number; totalProduced: number; totalSold: number; remainingPortions: number };
    wastage: { todayWastageCount: number; todayWastageValue: number; thisWeekWastageCount: number; thisWeekWastageValue: number; topWastageReasons: Array<{ reason: string; count: number }> };
    recentTransfers: FloorTransferSummary[];
    recentWastage: ProductionWastage[];
    recentBatches: any[];
}

export interface FloorTransferSummary {
    id: string;
    sourceType: 'branch' | 'production_floor';
    sourceName?: string;
    destinationType: 'branch' | 'production_floor';
    destinationName?: string;
    status: 'pending' | 'approved' | 'in_transit' | 'received' | 'cancelled';
    totalItems: number;
    totalQuantity: number;
    notes: string | null;
    createdAt: string;
}

export interface FloorTransferDetail extends FloorTransferSummary {
    items: FloorTransferItem[];
}

export interface FloorTransferItem {
    id: string;
    productId: string;
    productName?: string;
    quantity: string;
    uomId: string;
    uomName?: string;
}

export interface ProductionWastage {
    id: string;
    floorId: string;
    floorName?: string;
    batchId: string | null;
    batchNumber?: string;
    wastageType: 'raw_material' | 'production_loss' | 'spoilage' | 'unsold' | 'overproduction';
    productId: string;
    productName?: string;
    quantity: string;
    uomId: string;
    uomName?: string;
    costValue: string;
    reason: string;
    notes: string | null;
    reportedBy: string;
    reporterName?: string;
    createdAt: string;
}

interface StaffMember {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    roles: string[];
}

// ============================================================
// API FUNCTIONS
// ============================================================

export async function getProductionFloors(tenantId: string, params?: { status?: string; branchId?: string }): Promise<ProductionFloor[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/production-floors`, { params });
    return data?.data || [];
}

export async function getProductionFloor(tenantId: string, floorId: string): Promise<ProductionFloor> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/production-floors/${floorId}`);
    return data?.data;
}

export async function createProductionFloor(tenantId: string, payload: {
    branchId: string; name: string; floorType?: string; manager?: string;
    description?: string; contactPhone?: string; contactEmail?: string;
}): Promise<ProductionFloor> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/production-floors`, payload);
    return data?.data;
}

export async function updateProductionFloor(tenantId: string, floorId: string, payload: Partial<{
    name: string; floorType: string; manager: string | null; status: string;
    description: string | null; contactPhone: string | null; contactEmail: string | null;
}>): Promise<ProductionFloor> {
    const { data } = await apiClient.patch(`/tenants/${tenantId}/production-floors/${floorId}`, payload);
    return data?.data;
}

export async function deactivateProductionFloor(tenantId: string, floorId: string): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/production-floors/${floorId}`);
}

export async function getFloorDashboard(tenantId: string, floorId: string): Promise<FloorDashboard> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/production-floors/${floorId}/dashboard`);
    return data?.data;
}

export async function getFloorInventory(tenantId: string, floorId: string): Promise<FloorInventoryItem[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/production-floors/${floorId}/inventory`);
    return data?.data || [];
}

export async function getFloorTransfers(tenantId: string, params?: { limit?: number; status?: string; floorId?: string }): Promise<FloorTransferSummary[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/production-floors/transfers`, { params });
    return data?.data || [];
}

export async function getFloorTransfer(tenantId: string, transferId: string): Promise<FloorTransferDetail> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/production-floors/transfers/${transferId}`);
    return data?.data;
}

export async function createFloorTransfer(tenantId: string, payload: {
    sourceType: 'branch' | 'production_floor'; sourceId: string;
    destinationType: 'branch' | 'production_floor'; destinationId: string;
    items: Array<{ productId: string; quantity: string; uomId: string }>; notes?: string;
}): Promise<FloorTransferDetail> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/production-floors/transfers`, payload);
    return data?.data;
}

export async function cancelFloorTransfer(tenantId: string, transferId: string): Promise<void> {
    await apiClient.post(`/tenants/${tenantId}/production-floors/transfers/${transferId}/cancel`);
}

export async function getFloorWastage(tenantId: string, floorId: string, params?: { limit?: number; wastageType?: string; startDate?: string; endDate?: string }): Promise<ProductionWastage[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/production-floors/${floorId}/wastage`, { params });
    return data?.data || [];
}

export async function recordWastage(tenantId: string, floorId: string, payload: {
    batchId?: string; wastageType: string; productId: string; quantity: string;
    uomId: string; costValue?: string; reason: string; notes?: string;
}): Promise<ProductionWastage> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/production-floors/${floorId}/wastage`, payload);
    return data?.data;
}

export async function getManagers(tenantId: string): Promise<StaffMember[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/staff/managers`);
    return data?.data || [];
}